import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { doSignOutClear } from "../../components/auth/helpers";
import { ChatPreference } from "../../assets/interfaces/interfaces";
import { defaultPreferences } from "../../components/ChatMessage/constants";

type updateResponsePrefsArg = {
  session_id: string;
  preferences: ChatPreference;
};
type CustomizeResponseProps = {
  showSettings: boolean;
  showWarning: boolean;
  isFetching: boolean;
  isUpdating: boolean;
  savedSessionPreferences: {
    [session_id: string]: {
      preferences: ChatPreference;
    };
  };
};

const initialState: CustomizeResponseProps = {
  showSettings: false,
  showWarning: false,
  isFetching: false,
  isUpdating: false,
  savedSessionPreferences: {},
};

export const updateChatPreferences = createAsyncThunk(
  "responseCustomization/updateResponseCustomization",
  async (arg: updateResponsePrefsArg, { rejectWithValue, dispatch }) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_SPL_URL}/tradegpt-update-chat-preference/${arg.session_id}`,
        { preferences: arg.preferences },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      dispatch(setSavedPreferences({ session_id: arg.session_id, preferences: arg.preferences }));
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const fetchChatPreferences = createAsyncThunk(
  "responseCustomization/fetchChatPreferences",
  async (arg: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SPL_URL}/tradegpt-get-chat-preference/${arg}`,
        {
          withCredentials: true,
        },
      );
      let preferences = response.data.preferences;

      if (typeof preferences === "string") {
        preferences = JSON.parse(preferences);
      }

      preferences = preferences ?? {};
      dispatch(setSavedPreferences({ session_id: arg, preferences }));
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const responseCustomizationSlice = createSlice({
  name: "responseCustomization",
  initialState,
  reducers: {
    setShowSettings: (state, action) => {
      state.showSettings = action.payload;
    },
    setShowWarning: (state, action) => {
      state.showWarning = action.payload;
    },
    setSavedPreferences: (state, action) => {
      const { session_id, preferences } = action.payload;
      state.savedSessionPreferences[session_id]
      = !preferences || Object.keys(preferences).length === 0
          ? { ...defaultPreferences }
          : preferences;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatPreferences.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchChatPreferences.fulfilled, (state) => {
        state.isFetching = false;
      })
      .addCase(updateChatPreferences.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateChatPreferences.fulfilled, (state) => {
        state.isUpdating = false;
        state.showSettings = false;
      })
      .addCase(updateChatPreferences.rejected, (state) => {
        state.isUpdating = false;
      });
  },
});
export const {
  setShowSettings,
  setShowWarning,
  setSavedPreferences,
} = responseCustomizationSlice.actions;

export default responseCustomizationSlice.reducer;
